'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import {v4 as uuidV4} from 'uuid';
import parse from 'html-react-parser';

// prime react imports
import {
    Accordion,
    AccordionTab,
    AccordionTabChangeEvent
} from 'primereact/accordion';
import {Button} from 'primereact/button';
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {ChevronDownIcon} from 'primereact/icons/chevrondown';
import {ChevronRightIcon} from 'primereact/icons/chevronright';
import {ContextMenu} from 'primereact/contextmenu';
import {
    Dropdown,
    DropdownChangeEvent,
    DropdownProps
} from 'primereact/dropdown';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Panel} from 'primereact/panel';
import {Toolbar} from 'primereact/toolbar';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeEventNodeEvent,
    TreeSelectionEvent,
    TreeMultipleSelectionKeys
} from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import {TreeTableExpandedKeysType} from 'primereact/treetable';
import {IconUtils} from 'primereact/utils';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ItemType from '~/shared/src/mm/ItemType';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import Item, {
    ItemAttributesForCreation,
    ItemAttributesSettable
} from '~/shared/src/ci/Item';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import {fieldTypeToItemTypeMapping} from '~/shared/src/utilities/modules/form';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure,
    getDataObject,
    getDataStructurePath,
    getParentDataObject,
    getParentDataObjects
} from '~/shared/src/utilities/dataStructure';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {FilterConfigSet} from '~/shared/src/utilities/filterConfig';
import {FilterMatchMode} from '~/shared/src/validationSchemes/modules/overview';
import {
    getCustomFilterMode,
    getCustomFilters,
    getDefaultFilterMatchMode
} from '~/shared/src/utilities/filterUtils';
import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    parseCondition,
    ParsedCondition,
    ParsedConditionContent,
    ParsedConditions
} from '~/shared/src/utilities/modules/graph';

// frontend components import
import BooleanField from '~/frontend/src/components/base/shared/field/valueTypeFields/BooleanField';
import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import FilterField from '~/frontend/src/components/base/shared/field/FilterField';

// frontend imports
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getIconSpan,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    resolveLigatureIcon
} from '~/frontend/src/utilities/renderHelpers';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {graphUtilities} from '~/frontend/src/components/base/GraphDesigner/utilities';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    clearElusiveData,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';
import setPanelProps from '~/frontend/src/actions/panelManager/setPanelProps';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';


export type ExtendedDataObjectProperties = DataObjectProperties & {
    possibleItemTypeIds : number[];
    limitToItemTypeIds? : number[];
    resultItemTypeIds : number[];
    possibleReferenceSetIds? : number[];
    limitToReferenceSetIds? : number[];
    resultReferenceSetIds? : number[];
    referenceSetToItemTypeMapping? : { [ itemTypeId : number ] : number[] };
};
export type ExtendedDataStructure = ExtendedDataObjectProperties & {
    dataStructure? : ExtendedDataStructure[];
};

type ObjectDefinition = {
    [ id : string ] : boolean;
};
type FilterDefinition = {
    type : 'item' | 'itemType' | 'property' | 'reference' | 'referenceSet';
    key : string;
    label : string;
    icon : string;
    dataObjectId : number;
    attributePath : string;
    dataDescriptorAlias : string;
    valueType : PropertyValueType;
    params? : FilterConfigSet;
    value? : any;
    operator? : FilterMatchMode;
    conditionIds? : number[];
    error : boolean;
};

type ExtendTreeNode = Omit<TreeNode.TreeNode, 'data' | 'label'> & {
    key : string;
    label : React.ReactNode;
    tooltip? : React.ReactNode;
    dataObject? : ExtendedDataObjectProperties;
    children? : ExtendTreeNode[];
    data : {
        itemId : number;
        isDeleted : boolean;

        buttons : React.ReactNode[];
        itemTypes : ObjectDefinition;
        referenceSets : ObjectDefinition;
        filters : FilterDefinition[];
    };
};

type NodeData = {
    data : ExtendTreeNode;
    errorData : Required<Required<ElusiveData>['errorData']>;
};

type NodeButtons = {
    [ buttonId : string ] : {
        justInteracted : boolean;
        content? : ContentButtonType;
    };
};

export type LimitDataStructureTo = {
    [ dataObjectOrigId : number ] : {
        itemType? : {
            parsedCondition : ParsedConditionContent;
            ids : number[];
        };
        referenceSet? : {
            parsedCondition : ParsedConditionContent;
            ids : number[];
        }
    };
};

export type DefaultProps = {

    readOnly? : boolean;

    scrollTop? : number;
    scrollLeft? : number;

    expandedKeys? : TreeTableExpandedKeysType;
    selectionKeys? : TreeMultipleSelectionKeys;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    showItemTypeCheckboxes? : boolean;
    showReferenceSetCheckboxes? : boolean;
    showItemFilter? : boolean;
    showPropertyFilter? : boolean;
    accordionIndex? : { [ nodeKey : string ] : number | number[] };
};
export const defaultProps : Required<DefaultProps> = {
    readOnly                     : false,
    scrollTop                    : 0,
    scrollLeft                   : 0,
    expandedKeys                 : {},
    selectionKeys                : {},
    additionalElusiveDataContexts: [],
    showItemTypeCheckboxes       : true,
    showReferenceSetCheckboxes   : true,
    showItemFilter               : false,
    showPropertyFilter           : true,
    accordionIndex               : {}
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    rootItemId : number;

    selectedElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectElement? : ( id? : number ) => void;

    nodeIdToPanelKeyMap? : {
        [ nodeId : string ] : string
    };
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    treeData? : ExtendTreeNode[];
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const SearchTree = ( props : AllProps ) => {
    const dispatch : AppDispatch                        = useDispatch(),
          {
              accordionIndex,
              additionalElusiveDataContexts,
              dataContext,
              expandedKeys,
              nodeIdToPanelKeyMap,
              onSelectElement,
              parentUid,
              readOnly,
              rootItemId,
              scrollLeft,
              scrollTop,
              selectedElementId,
              selectionKeys,
              showItemFilter,
              showItemTypeCheckboxes,
              showPropertyFilter,
              showReferenceSetCheckboxes,
              uid
          }                                             = props,
          user                                          = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                       = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                            = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          elusiveData                                   = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          allElusiveData                                = useSelector( ( state : StoreState ) => state.elusiveData ),
          lastParams                                    = useRef<any>( null ),
          initialRendered                               = useRef<boolean>( false ),
          treeElement                                   = useRef<HTMLDivElement | null>( null ),
          contextMenu                                   = useRef<ContextMenu | null>( null ),
          nodeButtons                                   = useRef<NodeButtons>( {} ),
          ignoreSelectionChange                         = useRef<boolean>( false ),
          paramsToSet                                   = useRef<Partial<AllProps> | null>( null ),
          elusiveDataToSet                              = useRef<Partial<ElusiveData> | null>( null ),
          getScrollPositionTimeoutId                    = useRef<NodeJS.Timeout | undefined>(),
          changeFilterTimeoutId                         = useRef<NodeJS.Timeout | undefined>(),
          [ contextMenuNodeKey, setContextMenuNodeKey ] = useState<string | undefined>( undefined ),
          [ contextMenuNode, setContextMenuNode ]       = useState<ExtendTreeNode | null>( null ),
          language                                      = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                            = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          rootItem                                      = useMemo(
              () => {
                  return global.data[ dataContext ]?.items[ rootItemId ];
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          query                                         = useMemo(
              () => {
                  return graphUtilities.getQuery( dataContext, rootItemId );
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          condition                                     = useMemo(
              () => {
                  return graphUtilities.getCondition( dataContext, rootItemId );
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          dataSchema                                    = useMemo(
              () => {
                  return ( query?.dataStructure?.item.objectSchema ?? 'data' ).toUpperCase() as Schema;
              },
              [
                  query?.dataStructure
              ]
          ),
          parsedCondition                               = useMemo(
              () => {
                  return condition
                      ? parseCondition( condition )
                      : {} as ParsedConditions;
              },
              [
                  condition ? JSON.stringify( condition ) : ''
              ]
          ),
          limitDataStructureTo                          = useMemo(
              () => {
                  const retVal : LimitDataStructureTo = {};

                  if ( query?.dataStructure ) {
                      const dataObjects = extractDataObjectsFromDataStructure( query.dataStructure );

                      for ( const dataObject of dataObjects ) {
                          if ( parsedCondition[ dataObject.origId ] ) {
                              const itemTypeIdDataDescriptor     = buildDataDescriptor( { attributePath: 'item.itemTypeId' }, dataObject ),
                                    referenceSetIdDataDescriptor = buildDataDescriptor( { attributePath: 'reference.referenceSetId' }, dataObject );

                              if (
                                  itemTypeIdDataDescriptor
                                  && parsedCondition[ dataObject.origId ][ itemTypeIdDataDescriptor.alias ]?.operator === 'in'
                                  && parsedCondition[ dataObject.origId ][ itemTypeIdDataDescriptor.alias ].value
                              ) {
                                  retVal[ dataObject.origId ] = {
                                      ...retVal[ dataObject.origId ],
                                      itemType: {
                                          parsedCondition: {
                                              ...parsedCondition[ dataObject.origId ][ itemTypeIdDataDescriptor.alias ]
                                          },
                                          ids            : parsedCondition[ dataObject.origId ][ itemTypeIdDataDescriptor.alias ].value!.split( '-:,:-' ).map( v => Number( v.trim() ) )
                                      }
                                  };
                              }

                              if (
                                  referenceSetIdDataDescriptor
                                  && parsedCondition[ dataObject.origId ][ referenceSetIdDataDescriptor.alias ]?.operator === 'in'
                                  && parsedCondition[ dataObject.origId ][ referenceSetIdDataDescriptor.alias ].value
                              ) {
                                  retVal[ dataObject.origId ] = {
                                      ...retVal[ dataObject.origId ],
                                      referenceSet: {
                                          parsedCondition: {
                                              ...parsedCondition[ dataObject.origId ][ referenceSetIdDataDescriptor.alias ]
                                          },
                                          ids            : parsedCondition[ dataObject.origId ][ referenceSetIdDataDescriptor.alias ].value!.split( '-:,:-' ).map( v => Number( v.trim() ) )
                                      }
                                  };
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  parsedCondition ? JSON.stringify( parsedCondition ) : '',
                  query?.dataStructure
              ]
          ),
          extendedDataStructure                         = useMemo(
              () => {
                  const dataStructure = query?.dataStructure;

                  let retVal : ExtendedDataStructure | undefined = undefined;

                  if ( dataStructure ) {
                      const getExtendedDataStructure          = ( dataStructureToProcess : DataStructure, currentExtendedDataStructure? : ExtendedDataStructure ) => {
                                const origParentDataObjects                                                  = getParentDataObjects( dataStructureToProcess.id, dataStructure ),
                                      currentExtendedDataObjects                                             = currentExtendedDataStructure
                                          ? extractDataObjectsFromDataStructure( currentExtendedDataStructure ) as ExtendedDataObjectProperties[]
                                          : undefined,
                                      parentDataObjects                                                      = ( currentExtendedDataObjects
                                              ? origParentDataObjects.reduce(
                                                  ( acc, origParentDataObject ) => {
                                                      const dataObject = currentExtendedDataObjects.find( cEDO => cEDO.id === origParentDataObject.id );

                                                      if ( dataObject ) {
                                                          acc.push( dataObject );
                                                      }

                                                      return acc;
                                                  },
                                                  [] as ExtendedDataObjectProperties[]
                                              )
                                              : []
                                      ) as ExtendedDataObjectProperties[],
                                      possibleItemTypeIds : number[]                                         = [],
                                      resultItemTypeIds : number[]                                           = [],
                                      possibleReferenceSetIds : number[]                                     = [],
                                      resultReferenceSetIds : number[]                                       = [],
                                      referenceSetToItemTypeMapping : { [ itemTypeId : number ] : number[] } = {};

                                let innerRetVal : ExtendedDataStructure | undefined = undefined;

                                // process only if dataStructureToProcess has objectType
                                if ( dataStructureToProcess.item.objectType ) {

                                    // 1 - resolve possible itemTypes
                                    // 1.1 - resolve itemType(s) by objectType
                                    // 1.1.1 - get dedicated itemType
                                    if ( dataStructureToProcess.item.objectType !== '*' ) {
                                        const dataObjectItemType = metaModel[ dataSchema ].getItemType( dataStructureToProcess.item.objectType ?? '' );

                                        if ( dataObjectItemType ) {
                                            possibleItemTypeIds.push( dataObjectItemType.id );
                                        }
                                    }

                                    // 1.1.2 - get all itemTypes
                                    else {
                                        const itemTypes = metaModel[ dataSchema ].getItemTypes();

                                        for ( const itemType of itemTypes ) {
                                            possibleItemTypeIds.push( itemType.id );
                                        }
                                    }


                                    // 2 - resolve possible referenceSets and reduce possible itemTypes
                                    // if has reference configuration and has no parents or all parents are resolved
                                    if (
                                        dataStructureToProcess.reference?.referenceConfiguration
                                        && origParentDataObjects.length
                                        && origParentDataObjects.length === parentDataObjects.length
                                    ) {
                                        const referenceTypeNames        = Object.keys( dataStructureToProcess.reference.referenceConfiguration ) as string[],
                                              parentItemTypeIds         = parentDataObjects.reduce(
                                                  ( acc, parentDataObject ) => {
                                                      for ( const itemTypeId of parentDataObject.resultItemTypeIds ) {
                                                          if ( !acc.includes( itemTypeId ) ) {
                                                              acc.push( itemTypeId );
                                                          }
                                                      }

                                                      return acc;
                                                  },
                                                  [] as number[]
                                              ),
                                              outItemTypeIds : number[] = [];

                                        // 2.1 - resolve referenceSet(s) by referenceType(s)
                                        for ( const referenceTypeName of referenceTypeNames ) {
                                            const referenceConfiguration              = dataStructureToProcess.reference.referenceConfiguration[ referenceTypeName ],
                                                  referenceDirection                  = referenceConfiguration.referenceDirection === 'SrcToDest'
                                                      ? 'src'
                                                      : referenceConfiguration.referenceDirection === 'DestToSrc'
                                                          ? 'dest'
                                                          : 'both',
                                                  referenceRecursionMin               = referenceConfiguration.referenceRecursionMin ?? 1,
                                                  referenceRecursionMax               = referenceConfiguration.referenceRecursionMax ?? referenceRecursionMin,
                                                  recursionInItemTypeIds : number[][] = [ parentItemTypeIds ];

                                            // process always as recursive referencing
                                            for ( let recursionCount = 0; recursionCount < referenceRecursionMax; recursionCount++ ) {
                                                const currentInItemTypeIds : number[]  = recursionInItemTypeIds[ recursionCount ],
                                                      currentOutItemTypeIds : number[] = [],
                                                      referenceSets : ReferenceSet[]   = [];

                                                // resolve referenceSets for referenceTypeName and currentInItemTypeIds [and possibleItemTypeIds]
                                                for ( const inItemTypeId of currentInItemTypeIds ) {
                                                    // resolve open end referenceSets but not for the last recursion round if dedicated out itemTypes
                                                    if ( recursionCount < referenceRecursionMax - 1 || dataStructureToProcess.item.objectType === '*' ) {
                                                        // get referenceSets for specified referenceTypeName
                                                        if ( referenceTypeName !== '*' ) {
                                                            referenceSets.push( ...metaModel[ dataSchema ].getReferenceSetsForItemTypeAndReferenceType( inItemTypeId, referenceTypeName, referenceDirection ) );
                                                        }
                                                        // get all referenceSets
                                                        else {
                                                            referenceSets.push( ...metaModel[ dataSchema ].getReferenceSetsForItemType( inItemTypeId, referenceDirection ) );
                                                        }
                                                    }

                                                    // resolve fixed end ReferenceSets
                                                    else {
                                                        for ( const possibleItemTypeId of possibleItemTypeIds ) {
                                                            const innerReferenceSets : ReferenceSet[] = [];

                                                            // get referenceSets for specified referenceTypeName
                                                            if ( referenceTypeName !== '*' ) {
                                                                innerReferenceSets.push( ...metaModel[ dataSchema ].getReferenceSetsForItemTypesAndReferenceType( inItemTypeId, possibleItemTypeId, referenceTypeName, referenceDirection ) );
                                                            }
                                                            // get all referenceSets
                                                            else {
                                                                innerReferenceSets.push( ...metaModel[ dataSchema ].getReferenceSetsForItemTypes( inItemTypeId, possibleItemTypeId, referenceDirection ) );
                                                            }

                                                            for ( const innerReferenceSet of innerReferenceSets ) {
                                                                if ( !referenceSets.includes( innerReferenceSet ) ) {
                                                                    referenceSets.push( innerReferenceSet );
                                                                }
                                                            }
                                                        }
                                                    }
                                                }

                                                // add unique referenceSetIds to possibleReferenceSetIds,
                                                // get currentOutItemTypeIds
                                                // and allowedCurrentOutItemTypeIds (currentOutItemTypeIds limited to possibleItemTypeIds)
                                                for ( const referenceSet of referenceSets ) {
                                                    let itemTypeId : number | undefined = undefined;

                                                    if ( !possibleReferenceSetIds.includes( referenceSet.id ) ) {
                                                        possibleReferenceSetIds.push( referenceSet.id );
                                                    }

                                                    if ( referenceDirection === 'src' ) {
                                                        itemTypeId = referenceSet.destItemTypeId;
                                                    }
                                                    else if ( referenceDirection === 'dest' ) {
                                                        itemTypeId = referenceSet.srcItemTypeId;
                                                    }
                                                    else if ( currentInItemTypeIds.includes( referenceSet.srcItemTypeId ) ) {
                                                        itemTypeId = referenceSet.destItemTypeId;
                                                    }
                                                    else {
                                                        itemTypeId = referenceSet.srcItemTypeId;
                                                    }

                                                    if ( !currentOutItemTypeIds.includes( itemTypeId ) ) {
                                                        currentOutItemTypeIds.push( itemTypeId );
                                                    }

                                                    if ( !referenceSetToItemTypeMapping[ itemTypeId ] ) {
                                                        referenceSetToItemTypeMapping[ itemTypeId ] = [];
                                                    }
                                                    if ( !referenceSetToItemTypeMapping[ itemTypeId ].includes( referenceSet.id ) ) {
                                                        referenceSetToItemTypeMapping[ itemTypeId ].push( referenceSet.id );
                                                    }
                                                }

                                                // set next recursion inItemTypeIds
                                                recursionInItemTypeIds.push( currentOutItemTypeIds );

                                                // add unique currentOutItemTypeIds to outItemTypeIds
                                                if ( recursionCount >= referenceRecursionMin - 1 ) {
                                                    for ( const limitedOutItemTypeId of currentOutItemTypeIds ) {
                                                        if ( !outItemTypeIds!.includes( limitedOutItemTypeId ) ) {
                                                            outItemTypeIds.push( limitedOutItemTypeId );
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        // limit possibleItemTypes to outItemTypeIds
                                        for ( let i = possibleItemTypeIds.length - 1; i >= 0; i-- ) {
                                            if ( !outItemTypeIds.includes( possibleItemTypeIds[ i ] ) ) {
                                                possibleItemTypeIds.splice( i, 1 );
                                            }
                                        }
                                    }

                                    // limit to filtered itemTypes if
                                    resultItemTypeIds.push( ...possibleItemTypeIds );
                                    if ( limitDataStructureTo[ dataStructureToProcess.origId ]?.itemType?.ids.length ) {
                                        for ( let i = resultItemTypeIds.length - 1; i >= 0; i-- ) {
                                            if ( !limitDataStructureTo[ dataStructureToProcess.origId ].itemType!.ids.includes( resultItemTypeIds[ i ] ) ) {
                                                resultItemTypeIds.splice( i, 1 );
                                            }
                                        }
                                    }

                                    // limit to filtered referenceSets if
                                    resultReferenceSetIds.push( ...possibleReferenceSetIds );
                                    if ( limitDataStructureTo[ dataStructureToProcess.origId ]?.referenceSet?.ids.length ) {
                                        for ( let i = resultReferenceSetIds.length - 1; i >= 0; i-- ) {
                                            if ( !limitDataStructureTo[ dataStructureToProcess.origId ].referenceSet!.ids.includes( resultReferenceSetIds[ i ] ) ) {
                                                resultReferenceSetIds.splice( i, 1 );
                                            }
                                        }
                                    }

                                    // limit mapping to possible itemTypes
                                    for ( const itemTypeId of Object.keys( referenceSetToItemTypeMapping ) ) {
                                        if ( !possibleItemTypeIds.includes( Number( itemTypeId ) ) ) {
                                            delete referenceSetToItemTypeMapping[ itemTypeId ];
                                        }
                                    }

                                    // existing dataObject of currentExtendedDataStructure
                                    innerRetVal = currentExtendedDataStructure
                                        ? getDataObject( dataStructureToProcess.id, currentExtendedDataStructure ) as ExtendedDataObjectProperties
                                        : undefined;

                                    // create new dataobject
                                    if ( !innerRetVal ) {
                                        innerRetVal = {
                                            ...dataStructureToProcess
                                        } as ExtendedDataObjectProperties;
                                        delete innerRetVal.dataStructure;
                                    }

                                    innerRetVal = {
                                        ...innerRetVal,
                                        possibleItemTypeIds,
                                        limitToItemTypeIds    : limitDataStructureTo[ dataStructureToProcess.origId ]?.itemType?.ids.length
                                            ? limitDataStructureTo[ dataStructureToProcess.origId ].itemType!.ids
                                            : undefined,
                                        resultItemTypeIds,
                                        possibleReferenceSetIds,
                                        limitToReferenceSetIds: limitDataStructureTo[ dataStructureToProcess.origId ]?.referenceSet?.ids.length
                                            ? limitDataStructureTo[ dataStructureToProcess.origId ].referenceSet!.ids
                                            : undefined,
                                        resultReferenceSetIds,
                                        referenceSetToItemTypeMapping
                                    };
                                }

                                return innerRetVal;
                            },
                            getExtendedDataStructureRecursive = ( dataStructureToProcess : DataStructure, currentExtendedDataStructure? : ExtendedDataStructure ) => {
                                const innerRetVal = getExtendedDataStructure( dataStructureToProcess, currentExtendedDataStructure );

                                if (
                                    innerRetVal
                                    && dataStructureToProcess.dataStructure?.length
                                    && (
                                        dataStructureToProcess === dataStructure
                                        || innerRetVal?.possibleReferenceSetIds?.length
                                    )
                                ) {
                                    for ( const subDataStructure of dataStructureToProcess.dataStructure ) {
                                        const extendedSubDataStructure = getExtendedDataStructureRecursive( subDataStructure, innerRetVal );

                                        if ( extendedSubDataStructure ) {
                                            if ( !innerRetVal.dataStructure ) {
                                                innerRetVal.dataStructure = [];
                                            }

                                            innerRetVal.dataStructure.push( extendedSubDataStructure );
                                        }
                                    }
                                }

                                return innerRetVal;
                            };

                      retVal = getExtendedDataStructureRecursive( query.dataStructure );
                  }

                  return retVal;
              },
              [
                  dataSchema,
                  limitDataStructureTo,
                  query?.dataStructure
              ]
          ),
          addDataObject                                 = useCallback(
              ( dataObjectToUse : DataObjectProperties ) => {
                  const activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                        dataObjectItemTypeId                = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                        objectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                        objectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                        isReferencedToReferenceSetId        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, dataObjectItemTypeId, 'isReferencedTo', 'dest' )![ 0 ].id,
                        referenceConfigurationPropertySetId = metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( isReferencedToReferenceSetId, 'ReferenceConfiguration' )!.id;

                  const newItem = dispatch( addItem(
                      dataContext,
                      {
                          statusId  : activeStatusId,
                          itemTypeId: dataObjectItemTypeId,
                          key       : `${rootItem!.key}:${uuidV4().substring( 0, 8 )}`,
                          name      : 'catch all itemTypes',
                          properties: [
                              {
                                  propertySetId: objectTypePropertySetId,
                                  value        : '*',
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: objectSchemaPropertySetId,
                                  value        : dataSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  const parentReference = dispatch( addReference(
                      dataContext,
                      {
                          referenceSetId: isReferencedToReferenceSetId,
                          statusId      : activeStatusId,
                          srcItemId     : newItem.id,
                          destItemId    : dataObjectToUse.origId,
                          enumProperties: [
                              {
                                  referencePropertySetId: referenceConfigurationPropertySetId,
                                  properties            : [
                                      {
                                          key     : '*',
                                          value   : '{ "referenceDirection": "*" }',
                                          statusId: activeStatusId
                                      }
                                  ]
                              }
                          ]
                      } as ReferenceAttributesForCreation,
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  if ( parentReference ) {
                      newItem.registerReference( parentReference );
                  }

                  // select new element
                  onSelectElement?.( newItem.id );
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  dataSchema
              ]
          ),
          deleteOrUndeleteNode                          = ( nodeId : string, itemToUse : Item ) => {
              const referenceToParentDataObject = global.data[ dataContext ]?.references?.[ itemToUse.references.find(
                        ( referenceId ) => {
                            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                            let retVal : boolean = false;

                            if (
                                reference
                                && reference.srcItemId === itemToUse.id
                                && reference.referenceSet.referenceType.name === 'isReferencedTo'
                            ) {
                                retVal = true
                            }

                            return retVal;
                        }
                    ) ?? 0 ],
                    formPanelKey                = nodeIdToPanelKeyMap?.[ nodeId ]?.replace( '_attributesForm', '' );

              // undelete
              if ( itemToUse.isDeleted() ) {
                  if ( referenceToParentDataObject ) {
                      dispatch( updateReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          { statusId: referenceToParentDataObject.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }

                  dispatch( updateItem(
                      dataContext,
                      itemToUse.id,
                      { statusId: itemToUse.attributesModified.get( 'statusId' ) as number },
                      additionalElusiveDataContexts
                  ) );
              }

              // delete
              else {
                  if ( referenceToParentDataObject ) {
                      dispatch( deleteReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          true,
                          additionalElusiveDataContexts,
                          false
                      ) );
                  }

                  dispatch( deleteItem(
                      dataContext,
                      itemToUse.id,
                      true,
                      additionalElusiveDataContexts,
                      false,
                      false
                  ) );

                  // remove from selectionKeys if
                  if ( selectionKeys?.[ nodeId ] ) {
                      const newSelectionKeys = {
                          ...selectionKeys
                      };

                      delete newSelectionKeys[ nodeId ];

                      dispatch( setParams( uid, {
                          selectionKeys: newSelectionKeys
                      } ) );
                  }

                  // deselect if selected
                  if ( selectedElementId === itemToUse.id ) {
                      onSelectElement?.( undefined );
                  }
              }

              // clean up form
              if ( formPanelKey ) {
                  const formPanelKeyWithoutPrefix = formPanelKey.replace( parentUid + '_', '' ),
                        panelProps                = { ...getPanelProps( parentUid ) };

                  if ( panelProps ) {
                      // delete props of form
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix ];
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix + '_attributesForm' ];

                      // delete error of form
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey ];
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey + '_attributesForm' ];

                      setPanelProps( parentUid, panelProps );
                  }

                  // delete elusive data of form
                  dispatch( clearElusiveData( formPanelKey ) );
                  dispatch( clearElusiveData( formPanelKey + '_attributesForm' ) );
              }
          },
          buildNodeMarker                               = ( nodeId : string, type : 'filter' ) => {
              const retVal : React.ReactNode[] = [];

              let icon = 'target';

              switch ( type ) {
                  case 'filter':
                      icon = 'filter_alt';
                      break;
              }

              retVal.push( <Tooltip target={`#${nodeId}_${type}_tooltip`} showDelay={500} position="top">
                  {__( `YN.base.frontend.searchTree.nodeMarker.${type}.tooltip` )}
              </Tooltip> );

              retVal.push( <span
                  id={`${nodeId}_${type}_tooltip`}
                  className="yn-icon material-symbols-outlined yn-blue"
                  style={{ fontSize: '16px', width: '1.6rem', height: '1.6rem' }}
              >
                  {icon}
              </span> );

              return retVal;
          },
          buildNodeAddButton                            = ( nodeId : string, dataObjectToUse : DataStructure, dataStructureToUse : DataStructure ) => {
              const buttonId = `${uid}_${nodeId}_addButton`;

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon="pi pi-plus-circle yn-add"
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={() => {
                      addDataObject( dataObjectToUse );
                      return false;
                  }}
                  content={<></>}
              />;
          },
          buildNodeDeleteButton                         = ( nodeId : string, itemToUse : Item ) => {
              const buttonId = `${uid}_${nodeId}_deleteButton`;

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon={`pi ${itemToUse.isDeleted() ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`}
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={( event ) => {
                      let innerRetVal : boolean = true;

                      if ( event.altKey || itemToUse.isDeleted() ) {
                          deleteOrUndeleteNode( nodeId, itemToUse );

                          innerRetVal = false;
                      }

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}

                  content={!itemToUse.isDeleted()
                      ? <Panel
                          key={`${buttonId}_panel`}
                          id={`${buttonId}_panel`}

                          style={{ width: '25rem' }}
                          className="yn-confirm-option-panel"
                      >
                          <label>
                              <span className="pi pi-question-circle yn-blue"></span>
                              {__( 'YN.base.designer.tree.item.delete.text', { itemKey: itemToUse.key, itemName: itemToUse.name, itemDisplayAttributeValue: itemToUse.displayAttributeValue } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  deleteOrUndeleteNode( nodeId, itemToUse );

                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>
                      : null}
              />;
          },
          buildDataObjectNode                           = ( dataObjectToUse : ExtendedDataStructure, dataStructureToUse : ExtendedDataStructure, parsedConditionToUse? : ParsedCondition ) : NodeData | undefined => {
              let retVal : NodeData | undefined = undefined;

              if ( dataStructureToUse ) {
                  const parentDataObject         = getParentDataObject( dataObjectToUse.id, dataStructureToUse ),
                        dataStructurePath        = getDataStructurePath( dataObjectToUse.id, dataStructureToUse ),
                        dataObjectItem           = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                        dataObjectItemDeleted    = dataObjectItem.isDeleted(),
                        dataObjectKey            = dataObjectToUse.id,
                        nodeId                   = `DO_${dataObjectItem.key.replaceAll( ':', '-' )}`,
                        errorMessages : string[] = [];

                  let dataObjectItemType                           = metaModel[ dataSchema ].getItemType( dataObjectToUse.item.objectType ?? 0 ),
                      dataObjectNotItemType : ItemType | undefined = undefined,
                      attributePath : string,
                      dataDescriptor : DataDescriptor | undefined,
                      fieldValueType : PropertyValueType,
                      fieldItemType : string;

                  // try to get by limited to referenceSetIds
                  if ( !dataObjectItemType ) {
                      const referenceSetItemTypeIds = ( dataObjectToUse.limitToReferenceSetIds ?? [] )
                          .reduce(
                              ( acc, referenceSetId ) => {
                                  let itemTypeId : number | undefined = undefined;

                                  for ( itemTypeId of Object.keys( dataObjectToUse.referenceSetToItemTypeMapping ).map( i => Number( i ) ) as number[] ) {
                                      if ( dataObjectToUse.referenceSetToItemTypeMapping?.[ itemTypeId ]?.includes( referenceSetId ) ) {
                                          break;
                                      }
                                  }

                                  if ( itemTypeId !== undefined && !acc.includes( itemTypeId ) ) {
                                      acc.push( itemTypeId );
                                  }

                                  return acc;
                              },
                              [] as number[]
                          );

                      if ( referenceSetItemTypeIds.length === 1 ) {
                          dataObjectItemType = metaModel[ dataSchema ].getItemType( referenceSetItemTypeIds[ 0 ] );
                      }
                      else if ( referenceSetItemTypeIds.length === dataObjectToUse.possibleItemTypeIds.length - 1 ) {
                          for ( const itemTypeId of dataObjectToUse.possibleItemTypeIds ) {
                              if ( !referenceSetItemTypeIds.includes( itemTypeId ) ) {
                                  dataObjectNotItemType = metaModel[ dataSchema ].getItemType( itemTypeId );
                                  break;
                              }
                          }
                      }
                  }

                  let label = dataObjectItemType?.label,
                      icon  = `yn-tree-icon ${dataObjectItemType?.icon ?? 'icon-itemtype-detail'}`;

                  if ( !label ) {
                      if ( dataObjectNotItemType ) {
                          label = __( 'YN.base.frontend.searchTree.node.notItemType.label', { itemType: dataObjectNotItemType?.label } );
                      }
                      else {
                          label = __( 'YN.base.frontend.searchTree.node.label', { level: String( ( dataStructurePath?.length ?? 1 ) - 1 ) } );
                      }
                  }

                  if ( ( dataStructurePath.length ?? 1 ) > 1 ) {
                      icon = `yn-tree-icon material-symbols-outlined ::counter_${( dataStructurePath.length ?? 1 ) - 1}::`;
                  }

                  retVal = {
                      data     : {
                          id        : nodeId,
                          key       : nodeId,
                          dataObject: dataObjectToUse,
                          label     : parse( label ),
                          selectable: !dataObjectItemDeleted,
                          className : 'yn-dataobject-node',
                          icon      : resolveLigatureIcon( icon ),
                          data      : {
                              itemId       : dataObjectItem.id,
                              isDeleted    : dataObjectItemDeleted,
                              buttons      : [],
                              itemTypes    : {},
                              referenceSets: {},
                              filters      : []
                          }
                      },
                      errorData: {
                          count  : 0,
                          tooltip: [],
                          message: []
                      }
                  };

                  // filter marker
                  if ( Object.keys( parsedConditionToUse ?? {} ).length ) {
                      retVal.data.data.buttons.push( buildNodeMarker( nodeId, 'filter' ) );
                  }

                  // add button
                  if ( !readOnly && !dataObjectItemDeleted && dataStructurePath.length < 6 ) {
                      retVal.data.data.buttons.push( buildNodeAddButton( dataObjectKey, dataObjectToUse, dataStructureToUse ) );
                  }
                  // delete/restore button
                  if ( !readOnly && parentDataObject ) {
                      retVal.data.data.buttons.push( buildNodeDeleteButton( nodeId, dataObjectItem ) );
                  }

                  // filters

                  // itemType checkboxes
                  for ( const itemTypeId of dataObjectToUse.possibleItemTypeIds ) {
                      retVal.data.data.itemTypes[ String( itemTypeId ) ] = dataObjectToUse.resultItemTypeIds.includes( itemTypeId ) ?? false;
                  }

                  // referenceSet checkboxes
                  if ( dataObjectToUse.possibleReferenceSetIds?.length ) {
                      for ( const referenceSetId of dataObjectToUse.possibleReferenceSetIds ) {
                          retVal.data.data.referenceSets[ String( referenceSetId ) ] = dataObjectToUse.resultReferenceSetIds?.includes( referenceSetId ) ?? false;
                      }
                  }

                  // item filters
                  fieldValueType = 'STRING';
                  fieldItemType = 'StringField';
                  attributePath = 'item.key';
                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                  if ( dataDescriptor?.alias ) {
                      let filterError : boolean = false;

                      if ( ( dataUtils.getDataValueType( dataDescriptor, dataSchema ) ?? 'STRING' ) as PropertyValueType === 'INTEGER' ) {
                          fieldValueType = 'INTEGER';
                          fieldItemType = 'IntegerField';
                      }

                      if ( parsedConditionToUse?.[ dataDescriptor.alias ]?.ids?.[ 0 ] ) {
                          try {
                              dataExtraction.extractConditionData(
                                  global.data[ dataContext ].items[ parsedConditionToUse[ dataDescriptor.alias ].ids[ 0 ] ],
                                  dataContext,
                                  dataStructureToUse
                              );
                          }
                          catch ( e ) {
                              filterError = true;
                              errorMessages.push( __(
                                  'YN.base.frontend.graphDesigner.error.filter.text',
                                  {
                                      filterField: getItemKeyDisplayName( dataObjectItemType?.id ?? dataObjectNotItemType?.id ?? 0, dataSchema )
                                  }
                              ) );
                          }
                      }

                      retVal.data.data.filters.push( {
                          type               : 'item',
                          key                : 'key',
                          label              : __( 'YN.base.common.item.key' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                          dataObjectId       : dataObjectToUse.origId,
                          attributePath,
                          dataDescriptorAlias: dataDescriptor.alias,
                          valueType          : fieldValueType,
                          operator           : parsedConditionToUse?.[ dataDescriptor.alias ]?.operator,
                          value              : parsedConditionToUse?.[ dataDescriptor.alias ]?.value,
                          conditionIds       : parsedConditionToUse?.[ dataDescriptor.alias ]?.ids,
                          error              : filterError
                      } );
                  }

                  fieldValueType = 'STRING';
                  fieldItemType = 'StringField';
                  attributePath = 'item.name';
                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                  if ( dataDescriptor?.alias ) {
                      let filterError : boolean = false;

                      if ( parsedConditionToUse?.[ dataDescriptor.alias ]?.ids?.[ 0 ] ) {
                          try {
                              dataExtraction.extractConditionData(
                                  global.data[ dataContext ].items[ parsedConditionToUse[ dataDescriptor.alias ].ids[ 0 ] ],
                                  dataContext,
                                  dataStructureToUse
                              );
                          }
                          catch ( e ) {
                              filterError = true;
                              errorMessages.push( __(
                                  'YN.base.frontend.graphDesigner.error.filter.text',
                                  {
                                      filterField: getItemNameDisplayName( dataObjectItemType?.id ?? dataObjectNotItemType?.id ?? 0, dataSchema )
                                  }
                              ) );
                          }
                      }

                      retVal.data.data.filters.push( {
                          type               : 'item',
                          key                : 'name',
                          label              : __( 'YN.base.common.item.name' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                          dataObjectId       : dataObjectToUse.origId,
                          attributePath,
                          dataDescriptorAlias: dataDescriptor.alias,
                          valueType          : fieldValueType,
                          operator           : parsedConditionToUse?.[ dataDescriptor.alias ]?.operator,
                          value              : parsedConditionToUse?.[ dataDescriptor.alias ]?.value,
                          conditionIds       : parsedConditionToUse?.[ dataDescriptor.alias ]?.ids,
                          error              : filterError
                      } );
                  }

                  // property filters
                  if ( dataObjectItemType ) {
                      // item properties - get possible fields for all properties
                      const propertySets = metaModel[ dataSchema ].getPropertySetsForItemType( dataObjectItemType.id );

                      propertySets.sort(
                          ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                      );

                      for ( const propertySet of propertySets ) {
                          const params : FilterConfigSet = {
                              valueSetId: propertySet.valueSetId,
                              valueUnit : propertySet.valueUnit
                          };

                          attributePath = `property.${propertySet.name}`;
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              let filterError : boolean = false;

                              if ( propertySet.isEnumerated ) {
                                  fieldItemType = 'EnumField';
                              }
                              else if ( propertySet.valueSetId ) {
                                  fieldItemType = 'ValueSetField';
                              }
                              else {
                                  fieldItemType = fieldTypeToItemTypeMapping[ propertySet.valueType ];
                              }

                              if ( parsedConditionToUse?.[ dataDescriptor.alias ]?.ids?.[ 0 ] ) {
                                  try {
                                      dataExtraction.extractConditionData(
                                          global.data[ dataContext ].items[ parsedConditionToUse[ dataDescriptor.alias ].ids[ 0 ] ],
                                          dataContext,
                                          dataStructureToUse
                                      );
                                  }
                                  catch ( e ) {
                                      filterError = true;
                                      errorMessages.push( __(
                                          'YN.base.frontend.graphDesigner.error.filter.text',
                                          {
                                              filterField: propertySet.label
                                          }
                                      ) );
                                  }
                              }

                              retVal.data.data.filters.push( {
                                  type               : 'property',
                                  key                : propertySet.name,
                                  label              : propertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                                  dataObjectId       : dataObjectToUse.origId,
                                  attributePath,
                                  dataDescriptorAlias: dataDescriptor.alias,
                                  valueType          : propertySet.valueType,
                                  params,
                                  operator           : parsedConditionToUse?.[ dataDescriptor.alias ]?.operator,
                                  value              : parsedConditionToUse?.[ dataDescriptor.alias ]?.value,
                                  conditionIds       : parsedConditionToUse?.[ dataDescriptor.alias ]?.ids,
                                  error              : filterError
                              } );
                          }
                      }
                  }

                  if ( errorMessages.length ) {
                      const errorTitle = __( 'YN.base.frontend.graphDesigner.error.dataObject.title', {
                          dataObjectLabel: label
                      } );

                      retVal.errorData.count = errorMessages.length;
                      retVal.errorData.tooltip.push(
                          <div
                              key={`error_title_${retVal.errorData.count}`}
                              className="yn-tooltip-subheader"
                          >
                              {errorTitle}
                          </div>
                      );
                      retVal.errorData.tooltip.push(
                          <React.Fragment
                              key={`error_message_${retVal.errorData.count}`}
                          >
                              {errorMessages}
                          </React.Fragment>
                      );

                      retVal.errorData.message.push(
                          <div
                              key={`error_message_${retVal.errorData.count}`}
                              className="yn-error-suberror"
                          >
                              <div
                                  className="yn-error-subtitle"
                              >
                                  {errorTitle}
                              </div>
                              <div
                                  className="yn-error-message"
                              >
                                  {errorMessages}
                              </div>
                          </div>
                      );
                  }
              }

              return retVal;
          },
          buildTreeData                                 = ( dataStructureToUse : ExtendedDataStructure ) : ExtendTreeNode[] => {
              const retVal : ExtendTreeNode[]                         = [],
                    newExpandedKeys : TreeTableExpandedKeysType       = {},
                    newSelectionKeys                                  = { ...selectionKeys },
                    newErrorData : Required<ElusiveData['errorData']> = {
                        count  : 0,
                        tooltip: [],
                        message: []
                    };

              if ( dataStructureToUse ) {
                  const buildNodeDataRecursive = ( dataStructureToProcess : ExtendedDataStructure ) => {
                      const nodeData = buildDataObjectNode( dataStructureToProcess, dataStructureToUse, parsedCondition[ dataStructureToProcess.origId ] );

                      if ( nodeData ) {
                          newExpandedKeys[ nodeData.data.key ] = true;

                          if ( !Object.keys( newSelectionKeys ).length ) {
                              newSelectionKeys[ nodeData.data.key ] = true;
                          }

                          if ( nodeData.errorData?.count ) {
                              newErrorData.count += nodeData.errorData.count;

                              if ( !newErrorData.tooltip.length ) {
                                  newErrorData.tooltip.push(
                                      <div className="yn-tooltip-header2">
                                          {__( `YN.base.frontend.graphDesigner.error.dataStructure.tooltip.title` )}
                                      </div>
                                  );
                              }
                              newErrorData.tooltip = newErrorData.tooltip.concat( nodeData.errorData.tooltip );

                              if ( !newErrorData.message.length ) {
                                  newErrorData.message.push( <div className="yn-error-subtitle">
                                          {__( `YN.base.frontend.graphDesigner.error.dataStructure.message.title` )}
                                      </div>
                                  );
                                  newErrorData.message.push(
                                      <hr className="yn-error-ruler"/>
                                  );
                              }
                              else {
                                  newErrorData.message.push( <hr key={`error_ruler_${newErrorData.count}`} className="yn-error-ruler"/> );
                              }
                              newErrorData.message = newErrorData.message.concat( nodeData.errorData.message );
                          }

                          // process children only if not deleted
                          if ( !global.data[ dataContext ].items[ dataStructureToProcess.origId ].isDeleted() && dataStructureToProcess.dataStructure?.length ) {
                              nodeData.data.children = [];

                              for ( const childDataStructureToProcess of dataStructureToProcess.dataStructure ) {
                                  const childNodeData = buildNodeDataRecursive( childDataStructureToProcess );

                                  if ( childNodeData ) {
                                      newExpandedKeys[ childNodeData.data.key ] = true;
                                      nodeData.data.children.push( childNodeData.data );
                                  }
                              }

                              // sort by label
                              nodeData.data.children.sort( ( childNodeData1, childNodeData2 ) => {
                                  if ( ( childNodeData1.label ?? '' ) > ( childNodeData2.label ?? '' ) ) {
                                      return 1;
                                  }
                                  else if ( ( childNodeData2.label ?? '' ) > ( childNodeData1.label ?? '' ) ) {
                                      return -1;
                                  }
                                  else {
                                      return 0;
                                  }
                              } )
                          }
                      }

                      return nodeData;
                  };

                  const nodeData = buildNodeDataRecursive( dataStructureToUse );

                  if ( nodeData ) {
                      retVal.push( nodeData.data );
                  }
              }

              dispatch( setParams( uid, {
                  expandedKeys : newExpandedKeys,
                  selectionKeys: newSelectionKeys
              } ) );

              dispatch( setElusiveData( uid, {
                  errorData: newErrorData
              } ) );

              return retVal;
          },
          treeData                                      = useMemo(
              () => {
                  let retVal = elusiveData?.treeData;

                  // full (re-)build
                  if (
                      extendedDataStructure
                      && (
                          !retVal
                          || (
                              lastParams.current
                              && (
                                  dataContext !== lastParams.current.dataContext
                                  || globalDataVersions !== lastParams.current.globalDataVersions
                                  || extendedDataStructure !== lastParams.current.extendedDataStructure
                                  || parsedCondition !== lastParams.current.parsedCondition
                                  || language !== lastParams.current.language
                                  || messageCatalogTimestamp !== lastParams.current.messageCatalogTimestamp
                                  || metaModelTimestamp !== lastParams.current.metaModelTimestamp
                              )
                          )
                      )
                  ) {
                      retVal = buildTreeData( extendedDataStructure );

                      dispatch( setElusiveData( uid, { treeData: retVal } ) );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  extendedDataStructure,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  metaModelTimestamp,
                  parsedCondition
              ]
          ),
          addFilterCondition                            = useCallback(
              ( filter : FilterDefinition ) => {
                  const activeStatusId                                         = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                        conditionItemTypeId                                    = metaModel[ 'CFG' ].getItemType( 'Condition' )!.id,
                        cLeftExpressionPropertySetId                           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'LeftExpression' )!.id,
                        cOperatorPropertySetId                                 = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'Operator' )!.id,
                        cIsFilterForGraphReferenceSetId                        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, 'Graph', 'isFilterFor', 'src' )![ 0 ].id,
                        cIsLeftExpressionForCReferenceSetId                    = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, conditionItemTypeId, 'isLeftExpressionFor', 'src' )![ 0 ].id,
                        dataObjectProvidesDataForLeftExpressionCReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', conditionItemTypeId, 'providesDataForLeftExpression', 'src' )![ 0 ].id,
                        newCondition                                           = dispatch( addItem(
                            dataContext,
                            {
                                statusId  : activeStatusId,
                                itemTypeId: conditionItemTypeId,
                                key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                                name      : 'filter condition',
                                properties: [
                                    {
                                        propertySetId: cLeftExpressionPropertySetId,
                                        value        : filter.attributePath,
                                        statusId     : activeStatusId
                                    },
                                    {
                                        propertySetId: cOperatorPropertySetId,
                                        value        : filter.params?.valueSetId
                                            ? 'equals'
                                            : getDefaultFilterMatchMode( filter.valueType ),
                                        statusId     : activeStatusId
                                    }
                                ]
                            },
                            'CFG',
                            additionalElusiveDataContexts
                        ) );

                  // reference dataObject to new condition
                  dispatch( addReference(
                      dataContext,
                      {
                          referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                          statusId      : activeStatusId,
                          srcItemId     : filter.dataObjectId,
                          destItemId    : newCondition.id
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  let rootConditionId : number | undefined = condition?.id;

                  // add root condition if not
                  if ( !rootConditionId ) {
                      const rootCondition = dispatch( addItem(
                          dataContext,
                          {
                              statusId  : activeStatusId,
                              itemTypeId: conditionItemTypeId,
                              key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                              name      : 'filter root condition',
                              properties: [
                                  {
                                      propertySetId: cOperatorPropertySetId,
                                      value        : 'and',
                                      statusId     : activeStatusId
                                  }
                              ]
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      dispatch( addReference(
                          dataContext,
                          {
                              referenceSetId: cIsFilterForGraphReferenceSetId,
                              statusId      : activeStatusId,
                              srcItemId     : rootCondition.id,
                              destItemId    : rootItem.id
                          },
                          'CFG',
                          additionalElusiveDataContexts
                      ) );

                      rootConditionId = rootCondition.id;
                  }

                  // reference new condition to root condition
                  dispatch( addReference(
                      dataContext,
                      {
                          referenceSetId: cIsLeftExpressionForCReferenceSetId,
                          statusId      : activeStatusId,
                          srcItemId     : newCondition.id,
                          destItemId    : rootConditionId
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  return newCondition.id;
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItem,
                  uid
              ]
          ),
          changeFilterCondition                         = useCallback(
              ( filter : FilterDefinition, matchMode : FilterMatchMode, value : any ) => {
                  const activeStatusId                                         = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                        deletedStatusId                                        = metaModel[ 'CFG' ].getStatus( 'deleted' )!.id,
                        conditionItemTypeId                                    = metaModel[ 'CFG' ].getItemType( 'Condition' )!.id,
                        cLeftExpressionPropertySet                             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'LeftExpression' )!,
                        cOperatorPropertySet                                   = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'Operator' )!,
                        cRightExpressionPropertySet                            = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( conditionItemTypeId, 'RightExpression' )!,
                        cIsLeftExpressionForCReferenceSetId                    = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, conditionItemTypeId, 'isLeftExpressionFor', 'src' )![ 0 ].id,
                        cIsLRightExpressionForCReferenceSetId                  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( conditionItemTypeId, conditionItemTypeId, 'isRightExpressionFor', 'src' )![ 0 ].id,
                        dataObjectProvidesDataForLeftExpressionCReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', conditionItemTypeId, 'providesDataForLeftExpression', 'src' )![ 0 ].id;

                  // it's a non-custom condition
                  if ( matchMode !== 'custom' && matchMode !== 'in' ) {

                      if ( !filter.conditionIds ) {
                          // TODO: error
                      }

                      // was non-custom condition - just update
                      else if ( filter.operator !== 'custom' && filter.conditionIds.length === 1 ) {
                          const conditionItem = global.data[ dataContext ].items[ filter.conditionIds[ 0 ] ];

                          if ( conditionItem ) {
                              const properties : ItemAttributesSettable['properties'] = {
                                  [ cOperatorPropertySet.propertyType.id ]: {
                                      statusId: activeStatusId,
                                      value   : matchMode
                                  }
                              };

                              if ( matchMode === 'empty' || matchMode === 'notEmpty' ) {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: deletedStatusId
                                  };
                              }
                              else {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: activeStatusId,
                                      value   : value
                                  };
                              }

                              dispatch( updateItem(
                                  dataContext,
                                  conditionItem.id,
                                  {
                                      properties
                                  },
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      // was custom condition - get parent and update, delete conditions
                      else if ( filter.operator === 'custom' || filter.conditionIds.length > 1 ) {
                          const parentConditionId   = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, filter.conditionIds[ 0 ], undefined, undefined, false )?.[ 0 ]?.destItemId,
                                parentConditionItem = parentConditionId ? global.data[ dataContext ].items[ parentConditionId ] : undefined;

                          if ( parentConditionItem ) {
                              const properties : ItemAttributesSettable['properties'] = {
                                  [ cLeftExpressionPropertySet.propertyType.id ]: {
                                      statusId: activeStatusId,
                                      value   : filter.attributePath
                                  },
                                  [ cOperatorPropertySet.propertyType.id ]      : {
                                      statusId: activeStatusId,
                                      value   : matchMode
                                  }
                              };

                              if ( matchMode === 'empty' || matchMode === 'notEmpty' ) {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: deletedStatusId
                                  };
                              }
                              else {
                                  properties[ cRightExpressionPropertySet.propertyType.id ] = {
                                      statusId: activeStatusId,
                                      value   : value
                                  };
                              }
                              // delete conditions
                              for ( const conditionId of filter.conditionIds ) {
                                  dispatch( deleteItem(
                                      dataContext,
                                      conditionId,
                                      true,
                                      additionalElusiveDataContexts,
                                      false,
                                      true,
                                      false
                                  ) );
                              }

                              // update parent condition
                              dispatch( updateItem(
                                  dataContext,
                                  parentConditionItem.id,
                                  {
                                      properties
                                  },
                                  additionalElusiveDataContexts
                              ) );

                              // reference dataObject to parent condition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : filter.dataObjectId,
                                      destItemId    : parentConditionItem.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }
                  }

                  // it's a custom or in condition
                  else {
                      const joinOperator  = matchMode === 'in'
                                ? 'and'
                                : getCustomFilterMode( value ).toLowerCase(),
                            customFilters = matchMode === 'in'
                                ? ( value as unknown as number[] ).map( v => {
                                    return {
                                        value    : String( v ),
                                        matchMode: 'in'
                                    };
                                } )
                                : getCustomFilters( value, filter.valueType );

                      let parentConditionItem : Item | undefined

                      if ( !filter.conditionIds ) {
                          // TODO: error
                      }

                      // was non-custom condition - get condition as parent
                      else if ( filter.operator !== 'custom' && filter.operator !== 'in' && filter.conditionIds.length === 1 ) {
                          parentConditionItem = global.data[ dataContext ].items[ filter.conditionIds[ 0 ] ];
                      }

                      // was custom condition - get parent condition, delete conditions
                      else if ( filter.operator === 'custom' || filter.operator === 'in' || filter.conditionIds.length > 1 ) {
                          const parentConditionId = dataUtils.findReferences(
                              dataContext,
                              cIsLeftExpressionForCReferenceSetId,
                              filter.conditionIds[ 0 ],
                              undefined,
                              undefined,
                              false
                          )?.[ 0 ]?.destItemId;

                          parentConditionItem = parentConditionId ? global.data[ dataContext ].items[ parentConditionId ] : undefined;

                          if ( parentConditionItem ) {
                              // delete conditions
                              for ( const conditionId of filter.conditionIds ) {
                                  dispatch( deleteItem(
                                      dataContext,
                                      conditionId,
                                      true,
                                      additionalElusiveDataContexts,
                                      false,
                                      true,
                                      false
                                  ) );
                              }
                          }
                      }

                      if ( parentConditionItem && customFilters.length && rootItem ) {
                          dispatch( updateItem(
                              dataContext,
                              parentConditionItem.id,
                              {
                                  properties: {
                                      [ cLeftExpressionPropertySet.propertyType.id ] : {
                                          statusId: deletedStatusId
                                      },
                                      [ cOperatorPropertySet.propertyType.id ]       : {
                                          statusId: activeStatusId,
                                          value   : joinOperator
                                      },
                                      [ cRightExpressionPropertySet.propertyType.id ]: {
                                          statusId: deletedStatusId
                                      }
                                  }
                              },
                              additionalElusiveDataContexts
                          ) );

                          if ( matchMode === 'in' ) {
                              const newCondition = dispatch( addItem(
                                  dataContext,
                                  {
                                      statusId  : activeStatusId,
                                      itemTypeId: conditionItemTypeId,
                                      key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                                      name      : 'in filter condition',
                                      properties: [
                                          {
                                              propertySetId: cLeftExpressionPropertySet.id,
                                              statusId     : activeStatusId,
                                              value        : filter.attributePath
                                          },
                                          {
                                              propertySetId: cOperatorPropertySet.id,
                                              statusId     : activeStatusId,
                                              value        : 'in'
                                          }
                                      ]
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              // reference dataObject to condition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : filter.dataObjectId,
                                      destItemId    : newCondition.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              // reference condition to parentCondition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: cIsLeftExpressionForCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : newCondition.id,
                                      destItemId    : parentConditionItem.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              parentConditionItem = newCondition;
                          }

                          for ( const customFilter of customFilters ) {
                              const properties = [] as ItemAttributesForCreation['properties'];

                              if ( properties ) {
                                  if ( matchMode === 'in' ) {
                                      properties.push( {
                                          propertySetId: cLeftExpressionPropertySet.id,
                                          statusId     : activeStatusId,
                                          value        : customFilter.value
                                      } );
                                  }
                                  else {
                                      properties.push( {
                                          propertySetId: cLeftExpressionPropertySet.id,
                                          statusId     : activeStatusId,
                                          value        : filter.attributePath
                                      } );
                                      properties.push( {
                                          propertySetId: cOperatorPropertySet.id,
                                          statusId     : activeStatusId,
                                          value        : customFilter.matchMode
                                      } );

                                      if ( customFilter.matchMode !== 'empty' && customFilter.matchMode !== 'notEmpty' ) {
                                          properties.push( {
                                              propertySetId: cRightExpressionPropertySet.id,
                                              statusId     : activeStatusId,
                                              value        : customFilter.value
                                          } );
                                      }
                                  }
                              }

                              const newCondition = dispatch( addItem(
                                  dataContext,
                                  {
                                      statusId  : activeStatusId,
                                      itemTypeId: conditionItemTypeId,
                                      key       : `${rootItem.key}:${uuidV4().substring( 0, 8 )}`,
                                      name      : 'filter condition',
                                      properties
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );

                              // reference dataObject to condition
                              if ( matchMode !== 'in' ) {
                                  dispatch( addReference(
                                      dataContext,
                                      {
                                          referenceSetId: dataObjectProvidesDataForLeftExpressionCReferenceSetId,
                                          statusId      : activeStatusId,
                                          srcItemId     : filter.dataObjectId,
                                          destItemId    : newCondition.id
                                      },
                                      'CFG',
                                      additionalElusiveDataContexts
                                  ) );
                              }

                              // reference condition to parentCondition
                              dispatch( addReference(
                                  dataContext,
                                  {
                                      referenceSetId: matchMode === 'in'
                                          ? cIsLRightExpressionForCReferenceSetId
                                          : cIsLeftExpressionForCReferenceSetId,
                                      statusId      : activeStatusId,
                                      srcItemId     : newCondition.id,
                                      destItemId    : parentConditionItem.id
                                  },
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }
                  }
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItem
              ]
          ),
          removeFilterCondition                         = useCallback(
              ( filter : FilterDefinition ) => {
                  const cIsLeftExpressionForCReferenceSetId  = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'Condition', 'isLeftExpressionFor', 'src' )![ 0 ].id,
                        rootConditionId : number | undefined = condition?.id;

                  if ( !filter.conditionIds ) {
                      // TODO: error
                  }

                  // was non-custom condition - just delete
                  else if ( filter.operator !== 'custom' && filter.conditionIds.length === 1 ) {
                      dispatch( deleteItem(
                          dataContext,
                          filter.conditionIds[ 0 ],
                          true,
                          additionalElusiveDataContexts,
                          false,
                          true,
                          false
                      ) );
                  }

                  // was custom condition - get parent and delete
                  else if ( filter.operator === 'custom' || filter.conditionIds.length > 1 ) {
                      const parentConditionId   = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, filter.conditionIds[ 0 ], undefined, undefined, false )?.[ 0 ]?.destItemId,
                            parentConditionItem = parentConditionId ? global.data[ dataContext ].items[ parentConditionId ] : undefined;

                      if ( parentConditionItem ) {
                          // delete conditions
                          for ( const conditionId of filter.conditionIds ) {
                              dispatch( deleteItem(
                                  dataContext,
                                  conditionId,
                                  true,
                                  additionalElusiveDataContexts,
                                  false,
                                  true,
                                  false
                              ) );
                          }

                          dispatch( deleteItem(
                              dataContext,
                              parentConditionItem.id,
                              true,
                              additionalElusiveDataContexts,
                              false,
                              true,
                              false
                          ) );
                      }
                  }

                  const subExpressionReferences = dataUtils.findReferences( dataContext, cIsLeftExpressionForCReferenceSetId, undefined, rootConditionId, undefined, false );

                  // remove root condition if there are no other conditions left
                  if ( rootConditionId && ( !subExpressionReferences || subExpressionReferences.length === 0 ) ) {
                      dispatch( deleteItem(
                          dataContext,
                          rootConditionId,
                          true,
                          additionalElusiveDataContexts,
                          false,
                          true,
                          false
                      ) );
                  }
              },
              [
                  additionalElusiveDataContexts,
                  condition,
                  dataContext,
                  uid
              ]
          ),
          changeInCondition                             = useCallback(
              ( type : 'itemType' | 'referenceSet', dataObject : DataObjectProperties, values : number[] ) => {
                  const attributePath  = type === 'itemType'
                            ? 'item.itemTypeId'
                            : 'reference.referenceSetId',
                        dataDescriptor = buildDataDescriptor( { attributePath }, dataObject );

                  if ( dataDescriptor ) {
                      let conditionIds = limitDataStructureTo[ dataObject.origId ]?.[ type ]?.parsedCondition.ids;

                      // add / change
                      if ( values.length ) {
                          // add
                          if ( !conditionIds?.length ) {
                              conditionIds = [
                                  addFilterCondition( {
                                      type               : 'itemType',
                                      key                : 'itemType',
                                      label              : '',
                                      icon               : '',
                                      dataObjectId       : dataObject.origId,
                                      attributePath,
                                      dataDescriptorAlias: dataDescriptor.alias,
                                      valueType          : 'STRING',
                                      operator           : 'in',
                                      value              : '0',
                                      conditionIds       : [],
                                      error              : false
                                  } )
                              ];
                          }

                          // change
                          if ( conditionIds?.length ) {
                              changeFilterCondition(
                                  {
                                      type               : 'itemType',
                                      key                : 'itemType',
                                      label              : '',
                                      icon               : '',
                                      dataObjectId       : dataObject.origId,
                                      attributePath,
                                      dataDescriptorAlias: dataDescriptor.alias,
                                      valueType          : 'STRING',
                                      operator           : 'in',
                                      value              : '0',
                                      conditionIds,
                                      error              : false
                                  },
                                  'in',
                                  values
                              );
                          }
                      }

                      // delete
                      else if ( conditionIds?.length ) {
                          removeFilterCondition( {
                              type               : 'itemType',
                              key                : 'itemType',
                              label              : '',
                              icon               : '',
                              dataObjectId       : dataObject.origId,
                              attributePath,
                              dataDescriptorAlias: dataDescriptor.alias,
                              valueType          : 'STRING',
                              operator           : 'in',
                              value              : '0',
                              conditionIds,
                              error              : false
                          } );
                      }
                  }
              },
              [
                  addFilterCondition,
                  changeFilterCondition,
                  limitDataStructureTo,
                  removeFilterCondition
              ]
          ),
          setScrollPosition                             = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  if (
                      ( scrollTop && scrollElement.scrollHeight === scrollElement.clientHeight )
                      || ( scrollLeft && scrollElement.scrollWidth === scrollElement.clientWidth )
                  ) {
                      setTimeout(
                          setScrollPosition,
                          15
                      );
                  }
                  else {
                      scrollElement.scrollLeft = scrollLeft;
                      scrollElement.scrollTop = scrollTop;
                  }
              }
          },
          getScrollPosition                             = useCallback(
              () => {
                  const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

                  if ( scrollElement ) {
                      dispatch( setParams( uid, {
                          scrollTop : scrollElement.scrollTop ?? 0,
                          scrollLeft: scrollElement.scrollLeft ?? 0
                      } ) );
                  }
              },
              [
                  uid
              ]
          ),
          debouncedGetScrollPosition                    = useCallback(
              () => {
                  if ( getScrollPositionTimeoutId.current ) {
                      clearTimeout( getScrollPositionTimeoutId.current );
                  }
                  getScrollPositionTimeoutId.current = setTimeout(
                      () => {
                          getScrollPosition();
                      },
                      250
                  );
              },
              [
                  getScrollPosition
              ]
          ),
          onShowItemFilterCheckboxChange                = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  dispatch( setParam( uid, 'showItemFilter', event.checked! ) );
              },
              [
                  uid
              ]
          ),
          onShowItemTypeCheckboxesCheckboxChange        = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  dispatch( setParam( uid, 'showItemTypeCheckboxes', event.checked! ) );
              },
              [
                  uid
              ]
          ),
          onShowPropertyFilterCheckboxChange            = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  dispatch( setParam( uid, 'showPropertyFilter', event.checked! ) );
              },
              [
                  uid
              ]
          ),
          onShowReferenceSetCheckboxesCheckboxChange    = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  dispatch( setParam( uid, 'showReferenceSetCheckboxes', event.checked! ) );
              },
              [
                  uid
              ]
          ),
          footer                                        = useMemo(
              () => {
                  const layoutComponents : ToolbarLayoutComponents = {
                      left : [],
                      right: []
                  };

                  layoutComponents.left.push( {
                      type: 'genericNode',

                      params: <div
                          key={`${uid}_showItemTypeCheckboxesCheckbox`}
                          className="flex align-items-center field"
                          style={{ width: '100%' }}
                      >
                          <Checkbox
                              checked={showItemTypeCheckboxes}
                              onChange={onShowItemTypeCheckboxesCheckboxChange}
                              style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                          />
                          <label
                              htmlFor={`${uid}_showItemTypeCheckboxesCheckbox`}
                              className="block ml-3"
                              style={{ fontSize: '.8rem' }}
                          >
                              {__( 'YN.base.frontend.graphDesigner.showItemTypeCheckboxes.label' )}
                          </label>
                      </div>
                  } );

                  layoutComponents.left.push( {
                      type: 'genericNode',

                      params: <div
                          key={`${uid}_showItemFilterCheckbox`}
                          className="flex align-items-center field"
                          style={{ width: '100%' }}
                      >
                          <Checkbox
                              checked={showItemFilter}
                              onChange={onShowItemFilterCheckboxChange}
                              style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                          />
                          <label
                              htmlFor={`${uid}_showItemFilterCheckbox`}
                              className="block ml-3"
                              style={{ fontSize: '.8rem' }}
                          >
                              {__( 'YN.base.frontend.graphDesigner.showItemFilter.label' )}
                          </label>
                      </div>
                  } );

                  const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

                  return <Toolbar
                      start={toolbarComponents.left}
                      className="yn-panel-toolbar shadow-4"
                      style={{ zIndex: 5 }}
                  />;
              },
              [
                  language,
                  messageCatalogTimestamp,
                  onShowItemFilterCheckboxChange,
                  onShowItemTypeCheckboxesCheckboxChange,
                  onShowPropertyFilterCheckboxChange,
                  onShowReferenceSetCheckboxesCheckboxChange,
                  showItemFilter,
                  showItemTypeCheckboxes,
                  showPropertyFilter,
                  showReferenceSetCheckboxes,
                  uid
              ]
          ),
          onContextMenuSelectionChange                  = useCallback(
              ( event : TreeSelectionEvent ) => {
                  setContextMenuNodeKey( event.value + '' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onContextMenu                                 = useCallback(
              ( event : TreeEventNodeEvent ) => {
                  if ( !readOnly ) {
                      setContextMenuNode( event.node as ExtendTreeNode );
                      contextMenu.current?.show( event.originalEvent );
                  }
              },
              [
                  readOnly
              ]
          ),
          contextMenuModel                              = useMemo(
              () => {
                  const retVal : MenuItem[] = [];


                  if ( contextMenuNode?.dataObject && query?.dataStructure ) {
                      const dataObject = extractDataObjectsFromDataStructure( query.dataStructure ).find( dS => dS.id === contextMenuNode.dataObject!.id ) as DataStructure | undefined;

                      if ( dataObject ) {
                          const parentDataObject      = getParentDataObject( dataObject.id, query.dataStructure ),
                                dataObjectItem        = global.data[ dataContext ].items[ dataObject.origId ],
                                dataObjectItemDeleted = dataObjectItem.isDeleted();

                          // add button
                          if ( !readOnly && !dataObjectItemDeleted ) {
                              const buttonId   = `${uid}_${dataObject.id}_addButton`,
                                    menuItemId = `${dataObject.id}_addItem`;

                              retVal.push( {
                                  id     : menuItemId,
                                  label  : __( 'YN.base.designer.tree.item.add.title' ),
                                  icon   : 'pi pi-plus-circle yn-add',
                                  data   : {
                                      buttonId
                                  },
                                  command: ( e : MenuItemCommandEvent ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: false, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                              } );
                          }

                          // delete/restore button
                          if ( parentDataObject ) {
                              const buttonId = `${uid}_${dataObject.id}_deleteButton`;

                              if ( retVal.length ) {
                                  retVal.push( { separator: true } );
                              }

                              retVal.push( {
                                  id     : `${dataObject.id}_${dataObjectItemDeleted ? 'undeleteItem' : 'deleteItem'}`,
                                  label  : dataObjectItemDeleted ? __( 'YN.base.designer.tree.item.undelete.title' ) : __( 'YN.base.designer.tree.item.delete.title' ),
                                  icon   : `pi ${dataObjectItemDeleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`,
                                  data   : {
                                      buttonId
                                  },
                                  command: ( e : MenuItemCommandEvent ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent )
                              } );
                          }
                      }
                  }

                  return retVal;
              },
              [
                  contextMenuNode,
                  dataContext,
                  language,
                  messageCatalogTimestamp,
                  query?.dataStructure,
                  readOnly,
                  treeData,
                  uid
              ]
          ),
          onTreeNodeToggleClick                         = useCallback(
              ( key : string, itemId : number ) => {
                  const dataObjectItem   = global.data[ dataContext ].items[ itemId ],
                        newSelectionKeys = { ...selectionKeys };

                  if ( newSelectionKeys[ key ] ) {
                      delete newSelectionKeys[ key ];
                  }
                  else {
                      newSelectionKeys[ key ] = true
                  }

                  // deleted nodes could not be selected
                  if ( !dataObjectItem || dataObjectItem.statusId !== -1 ) {
                      dispatch( setParams( uid, {
                          selectionKeys: newSelectionKeys
                      } ) );
                  }
              },
              [
                  dataContext,
                  selectionKeys,
                  uid
              ]
          ),
          filterDropdownItemTemplate                    = useCallback(
              ( option : DropdownProps ) => {
                  let retVal : React.ReactNode = null;

                  if ( option ) {
                      const filter = option as FilterDefinition;

                      retVal = <div
                          style={{
                              display   : 'flex',
                              alignItems: 'center'
                          }}
                      >
                          {getIconSpan( filter.icon, { style: { display: 'inline-block', marginRight: '.5rem' } } )}
                          {filter.label}
                      </div>;
                  }

                  return retVal;
              },
              [
                  'onlyOnce'
              ]
          ),
          filterDropdownValueTemplate                   = useCallback(
              ( option : DropdownProps, dropdownProps : DropdownProps ) => {
                  let retVal : React.ReactNode = dropdownProps.placeholder;

                  if ( option ) {
                      const filter = option as FilterDefinition;

                      retVal = <div
                          style={{
                              display   : 'flex',
                              alignItems: 'center'
                          }}
                      >
                          {getIconSpan( filter.icon, { style: { display: 'inline-block', marginRight: '.5rem' } } )}
                          {filter.label}
                      </div>;
                  }

                  return retVal;
              },
              [
                  'onlyOnce'
              ]
          ),
          onAddFilter                                   = useCallback(
              ( event : DropdownChangeEvent ) => {
                  addFilterCondition( event.value as FilterDefinition );
              },
              [
                  addFilterCondition
              ]
          ),
          onChangeFilter                                = useCallback(
              ( filter : FilterDefinition, key : string, field : string, matchMode : FilterMatchMode, value : any ) => {
                  if ( changeFilterTimeoutId.current ) {
                      clearTimeout( changeFilterTimeoutId.current );
                  }
                  changeFilterTimeoutId.current = setTimeout(
                      () => {
                          changeFilterCondition( filter, matchMode, value );
                      },
                      250
                  );
              },
              [
                  changeFilterCondition
              ]
          ),
          onRemoveFilter                                = useCallback(
              ( filter : FilterDefinition ) => {
                  removeFilterCondition( filter );
              },
              [
                  removeFilterCondition
              ]
          ),
          onReferenceSetFilterChange                    = useCallback(
              ( dataObject : ExtendedDataObjectProperties, type : 'add' | 'remove', referenceSetIds : number[] ) => {
                  if ( dataObject?.possibleReferenceSetIds ) {
                      let newReferenceSetIds = dataObject.limitToReferenceSetIds
                              ? [ ...dataObject.limitToReferenceSetIds ]
                              : [ ...dataObject.possibleReferenceSetIds ],
                          clearValues        = true;

                      for ( const referenceSetId of referenceSetIds ) {
                          if ( type === 'remove' && newReferenceSetIds.includes( referenceSetId ) ) {
                              // remove from list
                              newReferenceSetIds.splice( newReferenceSetIds.indexOf( referenceSetId ), 1 );
                          }
                          else if ( type === 'add' && !newReferenceSetIds.includes( referenceSetId ) ) {
                              // add to list
                              newReferenceSetIds.push( referenceSetId );
                          }
                      }

                      // clear if all possible ids in new ones (= no filtering)
                      for ( const iId of dataObject.possibleReferenceSetIds ) {
                          if ( !newReferenceSetIds.includes( iId ) ) {
                              clearValues = false;
                              break;
                          }
                      }
                      if ( clearValues ) {
                          newReferenceSetIds = [];
                      }

                      changeInCondition( 'referenceSet', dataObject, newReferenceSetIds );
                  }
              },
              [
                  changeInCondition
              ]
          ),
          onAccordionIndexChange                        = useCallback(
              ( nodeKey : string, event : AccordionTabChangeEvent ) => {
                  const newAccordionIndex = {
                      ...accordionIndex,
                      [ nodeKey ]: event.index
                  };
                  dispatch( setParam( uid, 'accordionIndex', newAccordionIndex ) );
              },
              [
                  accordionIndex,
                  uid
              ]
          ),
          treeNodeTemplate                              = useCallback(
              ( treeNodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as ExtendTreeNode,
                        classNames      = ( nodeData.className ?? '' ).split( ' ' ),
                        labelClassNames = [ 'yn-label' ];

                  // handle DataObject nodes
                  const nodeItem                                               = global.data[ dataContext ].items[ nodeData.data.itemId ?? 0 ],
                        formPanelErrors                                        = allElusiveData?.[ `${parentUid}_form-${nodeData.data.itemId ?? 0}` ]?.errorData?.count > 0,
                        filterError                                            = !!nodeData.data.filters.find( f => f.error ),
                        itemTypeAndReferencesFilterContent : React.ReactNode[] = [],
                        filterContent : React.ReactNode[]                      = [];

                  let content : React.ReactNode = null,
                      tooltip : React.ReactNode = null;

                  // build node content if selected
                  if ( selectionKeys?.[ nodeData.key ] ) {
                      const itemTypeIds                                                   = Object.keys( nodeData.data.itemTypes ).sort( ( itemTypeId1, itemTypeId2 ) => {
                                const label1 = metaModel[ dataSchema ].getItemType( Number( itemTypeId1 ) )?.label.toUpperCase() ?? '',
                                      label2 = metaModel[ dataSchema ].getItemType( Number( itemTypeId2 ) )?.label.toUpperCase() ?? '';

                                if ( label1 > label2 ) {
                                    return 1;
                                }
                                else if ( label1 < label2 ) {
                                    return -1;
                                }
                                else {
                                    return 0;
                                }
                            } ),
                            toggleAllItemTypeAndReferenceSetsCheckbox : React.ReactNode[] = [];

                      let referenceSetLabels : { [ referenceSetId : number ] : string } = {};

                      const getReferenceSetsForItemType = ( itemTypeId : number ) => {
                          referenceSetLabels = {};

                          return ( nodeData.dataObject?.referenceSetToItemTypeMapping?.[ itemTypeId ] ?? [] )
                              .map( referenceSetId => {
                                  const referenceSet = metaModel[ dataSchema ].getReferenceSet( Number( referenceSetId ) )!;

                                  let label = '';

                                  if ( referenceSet.srcItemTypeId === itemTypeId ) {
                                      label += `${referenceSet.destItemType.label} `;
                                  }
                                  else {
                                      label += `${referenceSet.srcItemType.label} `;
                                  }

                                  if ( referenceSet.srcItemTypeId === referenceSet.destItemTypeId ) {
                                      label += __( 'YN.base.common.reference.directionMarkerBidirectional' );
                                  }
                                  else if ( referenceSet.srcItemTypeId === itemTypeId ) {
                                      label += __( 'YN.base.common.reference.directionMarkerReversed' );
                                  }
                                  else {
                                      label += __( 'YN.base.common.reference.directionMarker' );
                                  }

                                  label += ` ${referenceSet.referenceTypeLabel} `;

                                  if ( referenceSet.srcItemTypeId === referenceSet.destItemTypeId ) {
                                      label += __( 'YN.base.common.reference.directionMarkerBidirectional' );
                                  }
                                  else if ( referenceSet.srcItemTypeId === itemTypeId ) {
                                      label += __( 'YN.base.common.reference.directionMarkerReversed' );
                                  }
                                  else {
                                      label += __( 'YN.base.common.reference.directionMarker' );
                                  }

                                  referenceSetLabels[ referenceSetId ] = label;

                                  return referenceSet;
                              } )
                              .sort( ( referenceSet1, referenceSet2 ) => {
                                  const label1 = referenceSetLabels[ referenceSet1.id ].toUpperCase() ?? '',
                                        label2 = referenceSetLabels[ referenceSet2.id ].toUpperCase() ?? '';

                                  if ( label1 > label2 ) {
                                      return 1;
                                  }
                                  else if ( label1 < label2 ) {
                                      return -1;
                                  }
                                  else {
                                      return 0;
                                  }
                              } );
                      };

                      // build itemType checkboxes if
                      if ( showItemTypeCheckboxes && itemTypeIds.length > 1 ) {
                          const firstReferenceSetId = getReferenceSetsForItemType( Number( itemTypeIds[ 0 ] ) )[ 0 ].id;

                          let allValue : boolean | null = null;

                          if ( Object.values( nodeData.data.referenceSets ).filter( v => v === true ).length === nodeData.dataObject?.possibleReferenceSetIds?.length ) {
                              allValue = true;
                          }
                          else if ( Object.values( nodeData.data.referenceSets ).filter( v => v === true ).length === 0 ) {
                              allValue = false;
                          }

                          if ( showReferenceSetCheckboxes || showItemFilter ) {
                              toggleAllItemTypeAndReferenceSetsCheckbox.push( <div
                                      key="itemTypes"
                                      className="yn-searchtree-field-group"
                                      style={{ paddingBottom: '0' }}
                                  >
                                      {__( `YN.base.frontend.graphDesigner.filter.type.itemType.label` )}
                                  </div>
                              );
                          }

                          // add toggle all checkbox
                          toggleAllItemTypeAndReferenceSetsCheckbox.push( <BooleanField
                              key={`${uid}_itemTypeCheckbox_all`}
                              uid={`${uid}_itemTypeCheckbox_all`}

                              value={allValue}
                              onChange={( value : boolean | null ) => {
                                  if ( nodeData.dataObject?.possibleReferenceSetIds && value !== null ) {
                                      changeInCondition( 'referenceSet', nodeData.dataObject, value ? [ ...nodeData.dataObject.possibleReferenceSetIds ] : [ firstReferenceSetId ] );
                                  }
                              }}

                              triState={false}
                              dontDisplayFalseValue={true}
                              labelVisibility="hidden"
                              infoTextLayout="hidden"
                              isDirty={false}
                              autoComplete={false}
                              disabled={readOnly}
                              inputClassNames={[ 'yn-booleanfield-undefined-blue' ]}
                              style={{ margin: '0 0 0 calc(1rem + 14px)' }}
                          /> );

                          for ( const itemTypeId of itemTypeIds ) {
                              const numberItemTypeId                      = Number( itemTypeId ),
                                    itemType                              = metaModel[ dataSchema ].getItemType( numberItemTypeId ),
                                    checkboxesContent : React.ReactNode[] = [];

                              if ( itemType ) {
                                  const referenceSets = getReferenceSetsForItemType( numberItemTypeId ),
                                        itemTypeValue = referenceSets.reduce(
                                            ( acc, referenceSet ) => {
                                                if ( acc !== null ) {
                                                    if ( acc === undefined ) {
                                                        if ( nodeData.data.referenceSets[ referenceSet.id ] ) {
                                                            acc = true;
                                                        }
                                                        else {
                                                            acc = false;
                                                        }
                                                    }
                                                    else if ( acc !== nodeData.data.referenceSets[ referenceSet.id ] ) {
                                                        acc = null;
                                                    }
                                                }

                                                return acc;
                                            },
                                            undefined as boolean | undefined | null
                                        ),
                                        header        = <BooleanField
                                            key={`${uid}_itemTypeCheckbox_${itemTypeId}`}
                                            uid={`${uid}_itemTypeCheckbox_${itemTypeId}`}

                                            value={itemTypeValue}
                                            onChange={() => {
                                                onReferenceSetFilterChange( nodeData.dataObject!, itemTypeValue ? 'remove' : 'add', referenceSets.map( referenceSet => referenceSet.id ) );
                                            }}

                                            checkboxLabel={itemType.label}
                                            triState={false}
                                            dontDisplayFalseValue={true}
                                            labelVisibility="hidden"
                                            infoTextLayout="hidden"
                                            isDirty={false}
                                            autoComplete={false}
                                            disabled={readOnly}
                                            inputClassNames={[ 'yn-booleanfield-undefined-blue' ]}
                                            style={{ marginBottom: '0' }}
                                        />;

                                  if ( showReferenceSetCheckboxes ) {
                                      for ( const referenceSet of referenceSets ) {
                                          checkboxesContent.push(
                                              <BooleanField
                                                  key={`${uid}_referenceSetCheckbox_${referenceSet.id}`}
                                                  uid={`${uid}_referenceSetCheckbox_${referenceSet.id}`}

                                                  value={nodeData.data.referenceSets[ referenceSet.id ]}
                                                  onChange={() => {
                                                      onReferenceSetFilterChange( nodeData.dataObject!, nodeData.data.referenceSets[ referenceSet.id ] ? 'remove' : 'add', [ referenceSet.id ] );
                                                  }}

                                                  checkboxLabel={referenceSetLabels[ referenceSet.id ]}
                                                  triState={false}
                                                  dontDisplayFalseValue={true}
                                                  labelVisibility="hidden"
                                                  infoTextLayout="hidden"
                                                  isDirty={false}
                                                  autoComplete={false}
                                                  disabled={readOnly}
                                                  style={{ margin: '0' }}
                                              />
                                          );
                                      }
                                  }

                                  itemTypeAndReferencesFilterContent.push(
                                      <AccordionTab
                                          header={header}
                                      >
                                          {checkboxesContent}
                                      </AccordionTab>
                                  );
                              }
                          }
                      }

                      // build filter if
                      if ( showItemFilter ) {
                          const addableFilters = nodeData.data.filters.filter(
                                    f => {
                                        return f.operator === undefined
                                            && (
                                                ( f.type === 'item' && showItemFilter )
                                                || ( f.type === 'property' && showPropertyFilter )
                                            );
                                    } ),
                                setFilters     = nodeData.data.filters.filter( f => f.operator !== undefined );

                          if ( showItemTypeCheckboxes || showReferenceSetCheckboxes ) {
                              filterContent.push( <div
                                      key="filter"
                                      className="yn-searchtree-field-group"
                                  >
                                      {__( `YN.base.frontend.graphDesigner.filter.type.item.label` )}
                                  </div>
                              );
                          }

                          if ( addableFilters.length ) {
                              filterContent.push( <Dropdown
                                  key={`${uid}_${nodeData.key}_addFilterDropdown`}
                                  id={`${nodeData.key}_addFilterDropdown`}
                                  inputId={`${nodeData.key}_addFilterDropdown_i`}
                                  onChange={onAddFilter}
                                  options={addableFilters}
                                  dataKey="key"
                                  disabled={readOnly}
                                  placeholder={__( 'YN.base.frontend.searchTree.filter.add' )}
                                  valueTemplate={filterDropdownValueTemplate}
                                  itemTemplate={filterDropdownItemTemplate}
                                  style={{ width: 'calc(100% - 2rem )', margin: '0 1rem .5rem 1rem' }}
                              /> );
                          }

                          for ( const filter of setFilters ) {
                              filterContent.push( <FieldWrapper
                                  key={`${uid}_${filter.key}_filter`}
                                  uid={`${uid}_${filter.key}_filter`}
                                  label={filter.label}
                                  labelPosition="top"
                                  infoTextLayout="hidden"
                                  isDirty={false}
                                  autoComplete={false}
                                  disabled={readOnly}
                                  style={{ width: 'calc(100% - 2rem )', margin: '0 1rem .5rem 1rem' }}
                              >
                                  <div
                                      style={{
                                          display: 'flex'
                                      }}
                                  >
                                      <Button
                                          onClick={onRemoveFilter.bind( null, filter )}
                                          icon="pi pi-times"
                                          className="p-button-rounded p-button-danger p-button-text"
                                          style={{
                                              alignSelf  : 'center',
                                              flex       : '0 0 auto',
                                              marginRight: '.5rem'
                                          }}
                                      />
                                      <FilterField
                                          fieldKey={`${uid}_${filter.key}_filter_i`}
                                          field={`${uid}_${filter.key}_filter_i`}

                                          value={filter.value ?? ''}
                                          valueType={filter.valueType}
                                          matchMode={filter.operator}

                                          showMatchModeSelector={true}
                                          markDirty={false}
                                          markError={filter.error}

                                          params={{
                                              ...filter.params,
                                              schema,
                                              timezone              : defaultBaseRequestParameters.timezone,
                                              rawMode               : false,
                                              showAdditionalRawNames: false
                                          }}

                                          onChange={onChangeFilter.bind( null, filter )}
                                      />
                                  </div>
                              </FieldWrapper> );
                          }
                      }

                      if ( itemTypeAndReferencesFilterContent.length || filterContent.length ) {
                          content = <div
                              key={`${nodeData.key}_body`}
                              className="yn-node-content"
                              style={{ padding: '0' }}
                          >
                              {toggleAllItemTypeAndReferenceSetsCheckbox}
                              <Accordion
                                  multiple={true}
                                  activeIndex={accordionIndex[ nodeData.key ]}
                                  onTabChange={onAccordionIndexChange.bind( null, nodeData.key )}
                              >
                                  {itemTypeAndReferencesFilterContent}
                              </Accordion>
                              {filterContent}
                          </div>;
                      }
                  }

                  // build node tooltip
                  if ( nodeData.tooltip ) {
                      tooltip = <Tooltip target={`#${nodeData.key}`} showDelay={500} position="top">
                          {nodeData.tooltip}
                      </Tooltip>;
                  }

                  if ( selectionKeys?.[ nodeData.key ] ) {
                      if ( !classNames.includes( 'yn-searchtree-node-expanded' ) ) {
                          classNames.push( 'yn-searchtree-node-expanded' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-searchtree-node-expanded' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if ( nodeItem?.isDeleted() && nodeItem?.isModified() ) {
                      if ( !classNames.includes( 'yn-deleted' ) ) {
                          classNames.push( 'yn-deleted' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-deleted' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if ( formPanelErrors || filterError ) {
                      labelClassNames.push( 'yn-error' );
                  }
                  else if ( !( nodeItem?.isDeleted() && nodeItem?.isModified() ) && nodeItem?.isModified() ) {
                      labelClassNames.push( 'yn-dirty' );
                  }

                  if ( nodeData.data.itemId === selectedElementId ) {
                      if ( !classNames.includes( 'yn-searchtree-node-selected' ) ) {
                          classNames.push( 'yn-searchtree-node-selected' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-searchtree-node-selected' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  nodeData.className = classNames.join( ' ' );

                  // build node
                  return <>
                      {tooltip}
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                      <div
                          key={`${uid}_${nodeData.key}_header`}
                          className="flex flex-column flex-auto overflow-hidden yn-searchtree-header"
                          draggable="false"
                          onClick={() => {
                              if ( !ignoreSelectionChange.current ) {
                                  onSelectElement?.( nodeData.data.itemId );
                              }

                              ignoreSelectionChange.current = false;
                          }}
                          onMouseEnter={() => {
                          }}
                          onMouseLeave={() => {
                          }}
                      >
                          <div key={`${nodeData.key}`} id={`${nodeData.key}`} className="yn-treenode-header">
                              <span key="label" className={labelClassNames.join( ' ' )}>{nodeData.label ?? ''}</span>
                              {nodeData.data.buttons}
                          </div>
                      </div>
                      {content}
                  </>;
              },
              [
                  accordionIndex,
                  dataContext,
                  dataSchema,
                  filterDropdownItemTemplate,
                  filterDropdownValueTemplate,
                  language,
                  limitDataStructureTo,
                  messageCatalogTimestamp,
                  onAccordionIndexChange,
                  onAddFilter,
                  onChangeFilter,
                  onReferenceSetFilterChange,
                  onRemoveFilter,
                  onSelectElement,
                  readOnly,
                  selectedElementId,
                  selectionKeys,
                  showItemFilter,
                  showItemTypeCheckboxes,
                  showPropertyFilter,
                  showReferenceSetCheckboxes
              ]
          ),
          treeTogglerTemplate                           = useCallback(
              ( nodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const icon = IconUtils.getJSXIcon(
                      selectionKeys?.[ nodeData.key ?? '' ]
                          ? <ChevronDownIcon/>
                          : <ChevronRightIcon/>,
                      {},
                      {}
                  );

                  return <Button
                      className={`yn-tree-toggler p-tree-toggler p-link p-unselectable-text ${nodeData.selectable === false ? 'yn-disabled' : ''}`}
                      icon={icon}
                      onClick={() => {
                          onTreeNodeToggleClick( String( nodeData.key ?? '' ), nodeData.data.itemId );
                      }}>
                  </Button>
              },
              [
                  onTreeNodeToggleClick,
                  selectionKeys
              ]
          ),
          content                                       = useMemo(
              () => {
                  let retVal = <></>;

                  if ( extendedDataStructure ) {
                      retVal = <>
                          <ContextMenu
                              key={`${uid}_contextMenu`}
                              model={contextMenuModel}
                              ref={el => contextMenu.current = el}
                              onHide={() => setContextMenuNodeKey( undefined )}
                          />
                          <Tree
                              key={`${uid}_tree`}
                              id={`${uid}_tree`}

                              ref={el => treeElement.current = el?.getElement() ?? null}

                              value={treeData as TreeNode.TreeNode[]}
                              nodeTemplate={treeNodeTemplate}

                              expandedKeys={expandedKeys}
                              togglerTemplate={treeTogglerTemplate}
                              // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                              onToggle={() => {
                              }}

                              selectionMode="multiple"
                              selectionKeys={selectionKeys}

                              className="yn-node-content-tree yn-searchtree"

                              contextMenuSelectionKey={contextMenuNodeKey}
                              onContextMenuSelectionChange={onContextMenuSelectionChange}
                              onContextMenu={onContextMenu}

                              footer={footer}
                              pt={{
                                  container: {
                                      onScroll: debouncedGetScrollPosition
                                  },
                                  footer   : {
                                      style: {
                                          margin: '1rem -1rem -.5rem -1rem',
                                          width : 'calc(100% + 2rem)',
                                          zIndex: '10'
                                      }
                                  }
                              }}
                          />
                      </>;
                  }
                  else if ( rootItem && !query ) {
                      retVal = <div
                          className="yn-node-content-tree-empty"
                          style={{
                              fontSize : '1.5rem',
                              padding  : '2rem 1rem',
                              textAlign: 'center'
                          }}
                      >
                          {__( 'YN.base.frontend.searchTree.genericGraph.text' )}
                      </div>;
                  }

                  return retVal;
              },
              [
                  contextMenuModel,
                  contextMenuNodeKey,
                  debouncedGetScrollPosition,
                  expandedKeys,
                  extendedDataStructure,
                  footer,
                  onContextMenu,
                  onContextMenuSelectionChange,
                  readOnly,
                  selectionKeys,
                  setContextMenuNodeKey,
                  treeData,
                  treeNodeTemplate,
                  treeTogglerTemplate,
                  uid
              ]
          );

    lastParams.current = {
        condition,
        dataContext,
        dataSchema,
        extendedDataStructure,
        globalDataVersions,
        language,
        limitDataStructureTo,
        messageCatalogTimestamp,
        metaModelTimestamp,
        parsedCondition,
        query,
        rootItem,
        rootItemId,
        selectedElementId,
        selectionKeys,
        treeData,
        uid
    };

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    // remove observed element from resizeObserver
    useEffect(
        () => {

            // unmount
            return () => {
                if ( getScrollPositionTimeoutId.current ) {
                    clearTimeout( getScrollPositionTimeoutId.current );
                }
                if ( changeFilterTimeoutId.current ) {
                    clearTimeout( changeFilterTimeoutId.current );
                }
            }
        },
        []
    );

    // set scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            setScrollPosition();
        },
        [
            scrollLeft,
            scrollTop,
            initialRendered.current
        ]
    );

    // update treeData in elusiveData
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { treeData } ) );
        },
        [
            treeData
        ]
    );

    // update paramsToSet
    useEffect(
        () => {
            if ( paramsToSet.current ) {
                dispatch( setParams( uid, paramsToSet.current ) );
            }
            paramsToSet.current = null;
        },
        [
            paramsToSet.current
        ]
    );

    // update elusiveData
    useEffect(
        () => {
            if ( elusiveDataToSet.current ) {
                dispatch( setElusiveData( uid, elusiveDataToSet.current ) );
            }
            elusiveDataToSet.current = null;
        },
        [
            elusiveDataToSet.current
        ]
    );

    return content;
};
SearchTree.defaultProps = defaultProps;
SearchTree.displayName = 'SearchTree';


export default SearchTree;
